import React, { useState, FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import Octicon, { LineArrowRight } from '@primer/octicons-react'

import { StyledPicker } from './styles'
import { dateFormatter, serverDateFormatter } from '~/utils/formatters'

import { LOCALE, DATES_RANGE } from '~/types'
import * as rdrLocales from 'react-date-range/dist/locale'

interface Props {
  endDate?: string | undefined
  onSelect: (dates: DATES_RANGE) => void
  startDate?: string | undefined
  locale: LOCALE
}

const DateRange: FunctionComponent<Props> = ({
  endDate,
  onSelect,
  startDate,
  locale,
}: Props) => {
  const { t } = useTranslation(['common'])
  const [showCal, setShowCal] = useState(false)

  // Save startDate and endDate to state unless not provided
  const [dateFilter, setDateFilter] = useState([
    {
      startDate: startDate
        ? // Hack for Safari - https://stackoverflow.com/a/21884244/5650901
          new Date(startDate.split(' ').join('T'))
        : new Date(),
      endDate: endDate ? new Date(endDate.split(' ').join('T')) : new Date(),
      key: 'selection',
    },
  ])

  // Display date if startDate and endDates are passed as props
  const defaultDisplayValue =
    startDate && endDate ? '' : t('No dates are selected')

  const [displayValue, setDisplayValue] = useState(defaultDisplayValue)

  const handleDateButton = () => setShowCal(!showCal)

  const handleChange = item => {
    setDisplayValue('')
    setDateFilter([item.selection])
  }

  const handleApply = () => {
    setShowCal(false)
    const { startDate, endDate } = dateFilter[0]
    onSelect({
      startDate: serverDateFormatter(startDate),
      endDate: serverDateFormatter(endDate),
    })
  }

  return (
    <div className="relative flex items-center">
      <button
        className="flex items-center text-gray-800 px-3 py-2 rounded rounded-r-none border-t border-b border-l border-gray-400 text-sm"
        data-testid="date-range-button"
        onClick={handleDateButton}
      >
        {displayValue ? (
          displayValue
        ) : (
          <>
            {dateFormatter(dateFilter[0].startDate, locale)}
            <Octicon icon={LineArrowRight} className="mx-3" />
            {dateFormatter(dateFilter[0].endDate, locale)}
          </>
        )}
      </button>
      <button
        className="px-3 py-2 rounded rounded-l-none border-t border-b border-r border-l border-brand-500 bg-brand-500 hover:bg-brand-600 hover:border-brand-600 text-white font-bold text-sm"
        onClick={handleApply}
      >
        {t('Apply')}
      </button>
      {showCal && (
        <StyledPicker
          onChange={handleChange}
          showSelectionPreview={true}
          moveRangeOnFirstSelection={false}
          months={1}
          ranges={dateFilter}
          direction="vertical"
          className="shadow-lg rounded overflow-hidden"
          rangeColors={['#F64F2A']}
          locale={rdrLocales[locale]}
        />
      )}
    </div>
  )
}

export default DateRange
